import { useQueryClient } from '@tanstack/react-query';
import { onAuthStateChanged } from 'firebase/auth';
import { createContext, useContext, useEffect, useReducer, useRef } from 'react';

import { getFirebaseAuth, signInWithToken } from '@/lib/firebase/auth';
import { initializeUserSettingsObserver } from '@/lib/firebase/store/observers/user-settings-observer';
import { type State as FirebaseContextState, INITIAL_STATE, reducer } from './reducer';

type FirebaseContextValue = FirebaseContextState;

const FirebaseContext = createContext<FirebaseContextValue | null>(null);

interface FirebaseProviderProps {
  isAuthenticated: boolean;
  isLoadingAuthenticated: boolean;
  /** Surfaced when the user-settings snapshot listener dies (e.g. permission-denied). */
  onSettingsError?: (error: Error) => void;
}

export function FirebaseProvider({ children, isAuthenticated, isLoadingAuthenticated, onSettingsError }: React.PropsWithChildren<FirebaseProviderProps>) {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const currentUser = state.user;
  const firebaseAuth = getFirebaseAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (isLoadingAuthenticated) return;

    if (!isAuthenticated) {
      void firebaseAuth
        .signOut()
        .catch((error) => dispatch({ type: 'AUTH_ERROR', error }))
        .finally(() => dispatch({ type: 'SIGN_OUT_DONE' }));

      return;
    }

    if (currentUser) return;

    dispatch({ type: 'SIGNIN_START' });
    void signInWithToken()
      .catch((error) => dispatch({ type: 'SIGNIN_ERROR', error }))
      .finally(() => dispatch({ type: 'SIGNIN_DONE' }));
  }, [isLoadingAuthenticated, isAuthenticated, currentUser, firebaseAuth.signOut]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      firebaseAuth,
      (user) => dispatch({ type: 'AUTH_STATE_CHANGED', user }),
      (error) => dispatch({ type: 'AUTH_ERROR', error }),
    );

    return () => unsubscribe();
  }, [firebaseAuth]);

  const onSettingsErrorRef = useRef(onSettingsError);
  onSettingsErrorRef.current = onSettingsError;

  useEffect(() => {
    if (!currentUser?.uid) return;

    return initializeUserSettingsObserver(queryClient, currentUser.uid, (error) => onSettingsErrorRef.current?.(error));
  }, [currentUser?.uid, queryClient]);

  return <FirebaseContext.Provider value={state}>{children}</FirebaseContext.Provider>;
}
FirebaseProvider.displayName = 'FirebaseProvider';

export function useFirebase(): FirebaseContextValue {
  const ctx = useContext(FirebaseContext);
  if (!ctx) throw new Error('useFirebase must be used within FirebaseProvider');

  return ctx;
}
